import React from "react";
import { StyleProp, StyleSheet, TouchableOpacity, View, ViewStyle } from "react-native";
import ParsedText from "react-native-parsed-text";
import { uniqueId } from "lodash";

import { tMessage, tMessageUser } from "tmt_api/src/types/types.validator";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { getSafeUrl } from "src/util/functions";

import { convertNodeToReact, convertTextToDescendents } from "./utils";
import { PdStack, PdText } from "src/components/layout";
import MessageFile from "./MessageFile";
import { Avatar } from "./components";
import { openUrlInNewTab } from "src/util/linking";

const getUsername = (data: tMessageUser): { firstPart: string; secondPart?: string } => {
  if (data.type === "tmtAdmin") return { firstPart: "The Main Tab | Help" };
  if (data.type === "rep")
    return {
      firstPart: "Sales Rep",
      secondPart: data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : data.name,
    };
  if (data.type === "brand" || data.type === "retailer")
    return { firstPart: data.name || "", secondPart: data.firstName };
  return { firstPart: data.name || "The Main Tab | Activity" };
};

const MessageUser: React.FC<{ data: tMessageUser }> = ({ data }) => {
  const { firstPart, secondPart } = getUsername(data);

  if (secondPart) {
    return (
      <PdText variant="body-sm">
        <PdText variant="body-sm" style={styles.textBold}>
          {firstPart}
        </PdText>
        <PdText variant="body-sm">{`, ${secondPart}`}</PdText>
      </PdText>
    );
  }
  return (
    <PdText variant="body-sm" style={styles.textBold}>
      {firstPart}
    </PdText>
  );
};

export const Message: React.FC<{ value: string; isSender?: boolean; style?: StyleProp<ViewStyle> }> = ({
  value,
  isSender,
  style,
}) => {
  const descendents = convertTextToDescendents(value);

  const handleOnPressUrl = (url: string) => {
    openUrlInNewTab(getSafeUrl(url));
  };

  if (descendents && descendents?.length > 0) {
    // TODO: check considering links
    // if (isDescendentsEmpty(descendents)) return null;
    return (
      <View style={style}>
        {descendents.map((node) => (
          <React.Fragment key={uniqueId("node_")}>{convertNodeToReact(node, isSender)}</React.Fragment>
        ))}
      </View>
    );
  }

  return (
    <View style={style}>
      <ParsedText
        parse={[{ type: "url", style: styles.messageItemTextUrl, onPress: handleOnPressUrl }]}
        style={[styles.messageItemText, isSender ? { color: kColors.white } : {}]}
      >
        {value}
      </ParsedText>
    </View>
  );
};

const MessageItem: React.FC<{
  item?: tMessage;
  isSender?: boolean;
  isMobile?: boolean;
  onPressUserName?: (item: tMessage) => void;
  onPressUserAvatar?: (item: tMessage) => void;
}> = ({ item, isSender, isMobile, onPressUserName, onPressUserAvatar }) => {
  if (!item) {
    return null;
  }

  const hasFiles = (item.files?.length ?? 0) > 0;

  const handleOnPressUserAvatar = () => {
    onPressUserAvatar?.(item);
  };

  const handleOnPressUserName = () => {
    onPressUserName?.(item);
  };

  if (isSender) {
    return (
      <View style={styles.senderMessageItemContainer}>
        <View style={styles.senderMessageItem}>
          <PdStack
            gap={isMobile ? 0 : 2}
            direction={isMobile ? "vertical" : "horizontal"}
            isReversed={isMobile}
            style={styles.senderMessageTitle}
          >
            <TouchableOpacity onPress={handleOnPressUserName}>
              <MessageUser data={item.user} />
            </TouchableOpacity>
            {item.createdAt && (
              <PdText variant="body-sm" style={{ color: kColors.gray500 }}>
                {item.createdAt}
              </PdText>
            )}
          </PdStack>
          <Message isSender style={[styles.senderMessageContainer, hasFiles ? styles.mb2 : {}]} value={item.text} />
          {hasFiles && (
            <View style={styles.senderMessageFilesContainer}>
              {item.files?.map((file) => (
                <MessageFile key={file.url} data={file} onPress={() => openUrlInNewTab(file.url)} />
              ))}
            </View>
          )}
        </View>
        <TouchableOpacity onPress={handleOnPressUserAvatar}>
          <Avatar
            uri={item.user.avatar}
            name={item.user.name}
            size="lg"
            mobileStyle={styles.ml3}
            webStyle={styles.ml3}
          />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.messageItemContainer}>
      <TouchableOpacity onPress={handleOnPressUserAvatar}>
        <Avatar uri={item.user.avatar} name={item.user.name} size="lg" mobileStyle={styles.mr3} webStyle={styles.mr3} />
      </TouchableOpacity>
      <View style={styles.messageItem}>
        <PdStack
          gap={isMobile ? 0 : 2}
          direction={isMobile ? "vertical" : "horizontal"}
          isReversed={isMobile}
          style={styles.messageTitle}
        >
          <TouchableOpacity onPress={handleOnPressUserName}>
            <MessageUser data={item.user} />
          </TouchableOpacity>
          {item.createdAt && (
            <PdText variant="body-sm" style={{ color: kColors.gray500 }}>
              {item.createdAt}
            </PdText>
          )}
        </PdStack>
        <Message style={[styles.messageContainer, hasFiles ? styles.mb2 : {}]} value={item.text} />
        {hasFiles && (
          <View style={styles.messageFilesContainer}>
            {item.files?.map((file) => (
              <MessageFile key={file.url} data={file} onPress={() => openUrlInNewTab(file.url)} />
            ))}
          </View>
        )}
      </View>
    </View>
  );
};

export default MessageItem;

const styles = StyleSheet.create({
  mb2: {
    marginBottom: kStyleConst.kSpacing * 2,
  },
  messageContainer: {
    backgroundColor: kColors.lightGray,
    borderRadius: kStyleConst.kSpacing * 1.5,
    borderTopLeftRadius: 0,
    maxWidth: "100%",
    paddingHorizontal: kStyleConst.kSpacing * 3,
    paddingVertical: kStyleConst.kSpacing * 2,
  },
  messageFilesContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: kStyleConst.kSpacing,
  },
  messageItem: {
    alignItems: "flex-start",
    flex: 0.7,
    marginTop: kStyleConst.kSpacing,
  },
  messageItemContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    marginVertical: kStyleConst.kSpacing * 3,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    width: "100%",
  },
  messageItemText: {
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: "300",
    letterSpacing: 1.2,
    lineHeight: 25,
    width: "100%",
  },
  messageItemTextUrl: {
    textDecorationLine: "underline",
  },
  messageTitle: {
    marginBottom: kStyleConst.kSpacing,
  },
  ml3: {
    marginLeft: kStyleConst.kSpacing * 3,
  },
  mr3: {
    marginRight: kStyleConst.kSpacing * 3,
  },
  senderMessageContainer: {
    backgroundColor: kColors.brandGreen400,
    borderRadius: kStyleConst.kSpacing * 1.5,
    borderTopRightRadius: 0,
    maxWidth: "100%",
    paddingHorizontal: kStyleConst.kSpacing * 3,
    paddingVertical: kStyleConst.kSpacing * 2,
  },
  senderMessageFilesContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: kStyleConst.kSpacing,
    justifyContent: "flex-end",
  },
  senderMessageItem: {
    alignItems: "flex-end",
    flex: 0.7,
    marginTop: kStyleConst.kSpacing,
  },
  senderMessageItemContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginVertical: kStyleConst.kSpacing * 3,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    width: "100%",
  },
  senderMessageTitle: {
    alignItems: "flex-end",
    marginBottom: kStyleConst.kSpacing,
  },
  textBold: {
    fontWeight: "500",
  },
});
